import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { MessageCircle, Send, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export const CharacterChat = ({ story }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const prot = story?.protagonist || { name: "The Protagonist" };

  useEffect(() => {
    if (open && user) {
      api.get(`/stories/${story.story_id}/chat`).then(r => setMessages(r.data.messages || [])).catch(() => {});
    }
  }, [open, user, story?.story_id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async () => {
    if (!text.trim() || busy) return;
    const draft = text.trim();
    setText("");
    setBusy(true);
    setMessages(m => [...m, { role: "user", text: draft, created_at: new Date().toISOString() }]);
    try {
      const { data } = await api.post(`/stories/${story.story_id}/chat`, { message: draft });
      setMessages(m => [...m, { role: "character", character: data.character, text: data.reply, created_at: data.created_at }]);
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to reach the character";
      toast.error(msg);
      // roll back the optimistic user message
      setMessages(m => m.slice(0, -1));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="character-chat-open"
          className="fixed bottom-6 right-6 z-30 no-print inline-flex items-center gap-2 rounded-full bg-gradient-to-br from-rose-500 to-amber-500 text-black font-semibold px-4 h-12 shadow-[0_16px_40px_rgba(244,63,94,0.35)] hover:scale-[1.02] transition"
        >
          <MessageCircle className="h-4 w-4" />
          <span className="hidden sm:inline">Chat with {prot.name}</span>
          <span className="sm:hidden">Chat</span>
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md bg-[#0F1117] border-white/10 flex flex-col p-0">
        <SheetHeader className="p-5 border-b border-white/5">
          <SheetTitle className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-amber-500 to-rose-500 flex items-center justify-center text-black font-bold">
              {(prot.name || "?").slice(0,1)}
            </div>
            <div>
              <div className="font-serif text-lg text-white">{prot.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400">{story?.genre}</div>
            </div>
          </SheetTitle>
          <SheetDescription className="text-xs text-slate-500">
            Speak directly with the protagonist of this story. They stay in character.
          </SheetDescription>
        </SheetHeader>
        <div ref={scrollRef} data-testid="chat-scroll" className="flex-1 overflow-y-auto p-5 space-y-3">
          {messages.length === 0 && !busy && (
            <div className="text-center py-10 text-slate-500 text-sm">
              <Sparkles className="h-6 w-6 text-amber-400 mx-auto mb-2" />
              Speak to {prot.name}. They will answer in character.
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-amber-500/15 border border-amber-500/30 text-amber-50 rounded-br-md"
                  : "bg-white/5 border border-white/10 text-slate-100 font-serif italic rounded-bl-md"
              }`}>
                {m.text}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-2.5 text-sm text-slate-400 italic">
                <span className="inline-flex gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" style={{animationDelay: "150ms"}} />
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" style={{animationDelay: "300ms"}} />
                </span>
              </div>
            </div>
          )}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-4 border-t border-white/5 flex items-center gap-2">
          <Input
            data-testid="chat-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={user ? `Message ${prot.name}…` : "Sign in to chat"}
            disabled={!user || busy}
            className="bg-[#07080A] border-white/10"
          />
          <Button data-testid="chat-send" type="submit" disabled={!user || busy || !text.trim()} className="rounded-full bg-amber-500 hover:bg-amber-400 text-black h-10 w-10 p-0">
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
};
